import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Lightbulb, Code, Rocket, CheckCircle } from 'lucide-react';

const ProcessOverview: React.FC = () => {
  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Discovery & Strategy',
      description: 'We begin by understanding your brand, goals, and target audience to create a comprehensive digital strategy.',
      details: ['Brand Analysis', 'Market Research', 'Competitor Analysis', 'Strategy Development'],
    },
    {
      number: '02',
      icon: Lightbulb,
      title: 'Design & Planning',
      description: 'Our creative team designs stunning visuals and user experiences that reflect your luxury brand identity.',
      details: ['UI/UX Design', 'Brand Guidelines', 'Wireframing', 'Prototyping'],
    },
    {
      number: '03',
      icon: Code,
      title: 'Development & Build',
      description: 'We bring your vision to life using cutting-edge technology and best practices for optimal performance.',
      details: ['Frontend Development', 'Backend Development', 'Integration', 'Testing'],
    },
    {
      number: '04',
      icon: Rocket,
      title: 'Launch & Optimize',
      description: 'We launch your project and continuously monitor and optimize for maximum performance and results.',
      details: ['Deployment', 'Performance Monitoring', 'Analytics Setup', 'Ongoing Support'],
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            Our <span className="text-gradient">Process</span>
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
            We follow a proven methodology that ensures every project delivers exceptional results while maintaining the highest standards of quality and innovation.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: false }}
              className="relative group"
            >
              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-primary-300 to-accent-300 z-0 transform translate-x-4"></div>
              )}

              <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-neutral-100 group-hover:border-primary-200">
                {/* Step Number */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {step.number}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-r from-primary-100 to-accent-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="w-8 h-8 text-primary-600" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-serif font-semibold mb-4 group-hover:text-primary-600 transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {step.description}
                </p>

                {/* Details */}
                <ul className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-sm text-neutral-600">
                      <CheckCircle className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-serif font-bold mb-4">
              Why Our Process Works
            </h3>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Our structured approach ensures transparency, quality, and results at every stage of your project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">✓</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Transparent Communication</h4>
              <p className="text-neutral-600">
                Regular updates and clear communication keep you informed throughout the entire process.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-accent-500 to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">⚡</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Agile Methodology</h4>
              <p className="text-neutral-600">
                Flexible approach that adapts to your needs while maintaining quality and timeline commitments.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">🎯</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Results-Driven</h4>
              <p className="text-neutral-600">
                Every step is designed to deliver measurable results that align with your business objectives.
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center mt-16"
        >
          <Link to="/how-it-works" className="btn-primary inline-flex items-center">
            Learn More About Our Process
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </motion.div> */}
      </div>
    </section>
  );
};

export default ProcessOverview;
